// src/components/EventDetailSidebar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldAlert, X, Copy, Eye, EyeOff, Download } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import { SlackIcon } from 'lucide-react';
import { SiJira } from "react-icons/si";
import { ChevronDown, ChevronRight, AlertTriangle, Globe, Lock, Server, Hash, ArrowRight, Camera, Shield, FileText } from "lucide-react";

export default function EventDetailSidebar({
  open,
  eventId,
  jwtToken,
  onClose,
  size = "wide",
  detailUrl = "https://whiteintel.io/api/regular/app/v2/watchlist/get_watchlist_events_v4.php",
  contentsLicenseUrl = "https://whiteintel.io/api/regular/app/v2/account/subscription.php",
  backdropZ = 400,
  panelZ = 500,
  onStatusChange,
  onUpgradeClick,
  onOpenGlobalSearch,
}) {
  const [tab, setTab] = useState("Summary");
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [errorDetail, setErrorDetail] = useState("");
  const [detail, setDetail] = useState(null);
  const abortDetail = useRef(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [subLoadingContents, setSubLoadingContents] = useState(false);
  const [subErrorContents, setSubErrorContents] = useState("");
  const [hasContentsAccess, setHasContentsAccess] = useState(null);
  const [slackPushing, setSlackPushing] = useState(false);
  const [slackPushStatus, setSlackPushStatus] = useState(null);
  const [loadingContents, setLoadingContents] = useState(false);
  const [errorContents, setErrorContents] = useState("");
  const [contents, setContents] = useState([]);
  const [hasLoadedContents, setHasLoadedContents] = useState(false);
  const abortContents = useRef(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const canCall = open && Boolean(eventId) && Boolean(jwtToken);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, [open]);

  const meta = useMemo(() => {
    const v = (x) => (x === null || x === undefined || x === "" ? "—" : x);
    const d0 = detail || {};
    const d = { ...d0 };
    const evt = String(d0.event_type ?? "").trim().toLowerCase();
    if (evt === "email") d.breach_type = "emailAddress";
    let breach = String(d.breach_type ?? d.event_type ?? "").trim().toLowerCase();
    if (breach === "email") breach = "emailAddress";

    return {
      entry_type: v(d.entry_type),
      entry: v(d.entry ?? d.domain ?? d.email),
      breach_type: breach,
      event_date: v(fmtISO(d.event_date)),
      credentials_count: d.credentials_count !== undefined && d.credentials_count !== null
        ? Number(d.credentials_count).toLocaleString() : "—",
      event_count: d.event_count !== undefined && d.event_count !== null
        ? Number(d.event_count).toLocaleString() : "—",
      min_log_date: v(fmtISO(d.min_log_date)),
      max_log_date: v(fmtISO(d.max_log_date)),
      git_branch: d.git_branch,
      status: v(d.status),
      title: d.title || d.subject || d.entry || `Event #${eventId}`,
      id: d.id ?? eventId,
      event_type: d.event_type ?? "-",
      jira_ticket: d.jira_ticket ?? "-",
      event_type_raw: d.event_type ?? null,
      breach_type_raw: d.breach_type ?? null,
    };
  }, [detail, eventId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setDetail(null); setContents([]); setHasLoadedContents(false);
      setErrorDetail(""); setErrorContents(""); setSubErrorContents("");
      setHasContentsAccess(null); setTab("Summary");
    } else { setTab("Summary"); }
  }, [open, eventId]);

  useEffect(() => {
    if (!canCall) return;
    setLoadingDetail(true); setErrorDetail("");
    if (abortDetail.current) abortDetail.current.abort();
    const controller = new AbortController();
    abortDetail.current = controller;
    (async () => {
      try {
        const res = await fetch(detailUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
          body: JSON.stringify({ id: Number(eventId), mode: "single" }),
          signal: controller.signal,
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error(`AUTH_REDIRECT_${res.status}`); }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const ev = json?.event ?? (Array.isArray(json?.results) && json.results.length === 1 ? json.results[0] : null);
        if (!ev) throw new Error("Event not found");
        setDetail(ev);
      } catch (e) {
        if (e.name !== "AbortError") setErrorDetail(e.message || "Failed to load event");
      } finally { if (!controller.signal.aborted) setLoadingDetail(false); }
    })();
    return () => controller.abort();
  }, [open, eventId, jwtToken, detailUrl, canCall]);

  useEffect(() => {
    if (!canCall || tab !== "Contents") return;
    setSubLoadingContents(true); setSubErrorContents("");
    (async () => {
      try {
        const res = await fetch(contentsLicenseUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
          body: JSON.stringify({}),
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error(`AUTH_REDIRECT_${res.status}`); }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setHasContentsAccess(Boolean(extractContentsAccess(json)));
      } catch (e) {
        setHasContentsAccess(false);
        setSubErrorContents(e.message || "Failed to verify subscription");
      } finally { setSubLoadingContents(false); }
    })();
  }, [canCall, tab, jwtToken, contentsLicenseUrl]);

  useEffect(() => {
    if (!canCall || tab !== "Contents") return;
    if (hasContentsAccess !== true) return;
    setLoadingContents(true); setErrorContents("");
    if (abortContents.current) abortContents.current.abort();
    const controller = new AbortController();
    abortContents.current = controller;
    const body = {
      id: Number(eventId), mode: "contents",
      date_from: detail?.min_log_date || detail?.event_date || null,
      date_to: detail?.max_log_date || detail?.event_date || null,
    };
    (async () => {
      try {
        const res = await fetch(detailUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
          body: JSON.stringify(body), signal: controller.signal,
        });
        if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error(`AUTH_REDIRECT_${res.status}`); }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const isLookalike = String(detail?.breach_type ?? detail?.event_type ?? "").trim().toLowerCase() === "lookalike";
        if (isLookalike) { setContents(json); }
        else if (String(detail?.breach_type ?? detail?.event_type ?? "").trim().toLowerCase() === "git_leak") {
          setContents(Array.isArray(json?.results?.records) ? json.results.records : []);
        } else { setContents(Array.isArray(json?.results) ? json.results : []); }
        setHasLoadedContents(true);
      } catch (e) {
        if (e.name !== "AbortError") setErrorContents(e.message || "Failed to load contents");
      } finally { if (!controller.signal.aborted) setLoadingContents(false); }
    })();
    return () => controller.abort();
  }, [tab, canCall, hasContentsAccess, detail, eventId, jwtToken, detailUrl]);

  async function handleToggleStatus() {
    const newStatus = (meta.status || "").toLowerCase() === "resolved" ? "open" : "resolved";
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/watchlist/status_update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({ id: meta.id, status: newStatus }),
      });
      if ([401, 419, 440].includes(res.status)) { redirectToLogin(); throw new Error(`AUTH_REDIRECT_${res.status}`); }
      if (!res.ok) throw new Error("Failed to update status");
      setDetail((prev) => (prev ? { ...prev, status: newStatus } : prev));
      onStatusChange?.(meta.id, newStatus);
    } catch (e) { console.error(e); }
  }

  const handleDownloadGitLeaksCsv = async (findingId) => {
    const jwtToken = localStorage.getItem("token");
    try {
      setDownloadingCsv(true);
      const response = await fetch("https://whiteintel.io/api/regular/app/v2/watchlist/download_git_leak_contents.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
        body: JSON.stringify({ finding_id: findingId }),
      });
      if (!response.ok) throw new Error("Failed to generate CSV");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "git_leaks_export.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Download failed:", err); }
    finally { setDownloadingCsv(false); }
  };

  const handlePushToJira = async () => {
    setPushing(true);
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/jira/create_jira_ticket_events.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ event_id: Number(eventId) }),
      });
      const raw = await res.text();
      let result = {}; try { result = raw ? JSON.parse(raw) : {}; } catch (_e) { result = {}; }
      if (!res.ok || result.success !== true) {
        setPushStatus({ type: "error", message: result?.error || result?.message || `Failed to create Jira ticket (HTTP ${res.status}).` });
        return;
      }
      const ticketSuffix = result.issueKey ? ` (${result.issueKey})` : "";
      setPushStatus({ type: "success", message: `${result.message || "Jira ticket created successfully"}${ticketSuffix}`, issueKey: result.issueKey, issueUrl: result.issueUrl });
      meta.jira_ticket = result.issueKey;
    } catch (err) { setPushStatus({ type: "error", message: "Unexpected error while pushing to Jira." }); }
    finally { setPushing(false); }
  };

  const handlePushToSlack = async () => {
    setSlackPushing(true);
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/slack/send_slack_events.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ event_id: Number(eventId) }),
      });
      const raw = await res.text();
      let result = {}; try { result = raw ? JSON.parse(raw) : {}; } catch (_e) { result = {}; }
      if (!res.ok || result.success !== true) {
        setSlackPushStatus({ type: "error", message: result?.error || result?.message || `Failed to send Slack alert (HTTP ${res.status}).` });
        return;
      }
      setSlackPushStatus({ type: "success", message: result.message || "Slack alert sent successfully" });
    } catch (err) { setSlackPushStatus({ type: "error", message: "Unexpected error while sending Slack alert." }); }
    finally { setSlackPushing(false); }
  };

  const handleDownloadCsv = () => {
    if (!Array.isArray(contents) || contents.length === 0) return;
    try {
      setDownloadingCsv(true);
      const headers = ["url", "username", "password", "log_date", "source"];
      const lines = [headers.join(",")];
      contents.forEach((r) => {
        lines.push([safeCsv(r.url), safeCsv(r.username), safeCsv(r.password), safeCsv(fmtISO(r.log_date)), safeCsv((r.source || r.type || "").toString().toLowerCase())].join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `event_${meta.id}_credentials.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setDownloadingCsv(false); }
  };

  return (
    <>
      {open && createPortal(
        <div role="presentation" onClick={onClose} aria-hidden="true" style={{ zIndex: backdropZ }}
          className="fixed inset-0 bg-black/60 transition-opacity opacity-100" />, document.body
      )}
      {open && createPortal(
        <aside role="dialog" aria-modal="true" style={{ zIndex: panelZ }}
          className={`fixed right-0 top-0 h-full w-full ${size === "wide" ? "max-w-4xl" : "max-w-2xl"}
            border-l border-neutral-800 bg-[#000414] shadow-[0_0_15px_rgba(56,189,248,0.05)]
            transform transition-transform duration-300 translate-x-0`}>

          {/* Header */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-800">
            <div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <ShieldAlert className="h-4 w-4" /><span>Event Detail — {meta.id}</span>
              </div>
              <h2 className="mt-1 text-xl font-semibold">
                {meta?.breach_type === "lookalike" ? <>Look-alike Domain Detected for {meta?.title}</>
                  : meta?.breach_type === "git_leak" ? <>Exposed Secrets Detected for {meta?.title}</>
                  : <>{(meta?.breach_type ? meta.breach_type.charAt(0).toUpperCase() + meta.breach_type.slice(1) : "")} Credential Exposure Details for {meta?.title}</>}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${(meta.status || "").toLowerCase() === "resolved" ? "bg-green-600/20 text-green-300" : "bg-yellow-600/20 text-yellow-300"}`}>
                {meta.status || "open"}
              </span>
              <button onClick={handleToggleStatus} className="rounded-md border border-sky-600/40 bg-sky-500/10 px-2 py-1 text-xs text-sky-200 hover:bg-sky-500/20">
                {(meta.status || "").toLowerCase() === "resolved" ? "Mark as Open" : "Mark as Resolved"}
              </button>
              <SmallSpinBtn onClick={handlePushToJira} loading={pushing} icon={<SiJira className="w-4 h-4" />} />
              <SmallSpinBtn onClick={handlePushToSlack} loading={slackPushing} icon={<SlackIcon className="w-4 h-4" />} />
              <IconBtn title="Close" onClick={onClose}><X className="h-4 w-4" /></IconBtn>
            </div>
          </div>

          {/* Push status banners */}
          <StatusBanner status={pushStatus} onDismiss={() => setPushStatus(null)} />
          <StatusBanner status={slackPushStatus} onDismiss={() => setSlackPushStatus(null)} />

          {/* Tabs */}
          <div className="px-5">
            <nav className="flex items-center gap-6 text-sm">
              {["Summary", "Contents"].map((t) => {
                const active = t === tab;
                return (
                  <button key={t} onClick={() => setTab(t)}
                    className={`py-3 ${active ? "text-white" : "text-neutral-400 hover:text-neutral-200"}`}>
                    {t}
                    <div className={`mt-2 h-[2px] rounded-full ${active ? "bg-white/70" : "bg-transparent"}`} />
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-5 pb-24 h-[calc(100%-100px)] relative">
            {tab === "Summary" && (
              <>
                {loadingDetail ? <Loader /> : errorDetail ? (
                  <div className="p-5 text-red-400 text-sm">Error: {errorDetail}</div>
                ) : (
                  <div className="space-y-8">
                    <Section title="What Happened">
                      {meta?.breach_type === "lookalike" ? (
                        <p className="text-sm text-neutral-300 leading-relaxed">
                          This alert was generated by our look-alike domain detection. We identified a domain that
                          resembles your protected asset and may be used for impersonation or phishing. Review the
                          metadata below, then open the <span className="text-neutral-200 font-medium">Contents</span> tab
                          to inspect the flagged domain(s) with full evidence: WHOIS records, SSL certificates, DNS resolution,
                          favicon fingerprints, redirect chains, HTTP headers, and screenshots.
                          If the domain appears malicious, consider initiating takedown and updating mail/web filters.
                        </p>
                      ) : meta?.breach_type === "git_leak" ? (
                        <p className="text-sm text-neutral-300 leading-relaxed">
                          This alert was triggered by our Git repository scanner. One or more secrets were detected
                          in your monitored code repositories. Review the metadata below, then open the <span className="text-neutral-200 font-medium">Contents</span> tab.
                        </p>
                      ) : (
                        <p className="text-sm text-neutral-300 leading-relaxed">
                          This event was recorded as part of our continuous monitoring. Review the metadata below,
                          then open the <span className="text-neutral-200 font-medium">Contents</span> tab to inspect the
                          affected credentials.
                        </p>
                      )}
                    </Section>
                    <Section title="Meta Data">
                      <MetaGrid items={
                        meta?.breach_type === "lookalike"
                          ? [
                              { label: "Entry Type", value: "domain" },
                              { label: "Entry", value: meta?.entry },
                              { label: "Finding Type", value: meta?.breach_type },
                              { label: "Event Date", value: meta?.event_date },
                              { label: "Event Type", value: meta?.event_type },
                              { label: "Status", value: meta?.status },
                              { label: "Jira Ticket", value: meta?.jira_ticket },
                            ]
                          : meta?.breach_type === "git_leak"
                          ? [
                              { label: "Entry Type", value: meta?.entry_type },
                              { label: "Entry", value: meta?.entry },
                              { label: "Git Branch", value: meta?.git_branch },
                              { label: "Finding Type", value: meta?.breach_type },
                              { label: "Event Date", value: meta?.event_date },
                              { label: "Event Type", value: meta?.event_type },
                              { label: "Status", value: meta?.status },
                              { label: "Jira Ticket", value: meta?.jira_ticket },
                            ]
                          : [
                              { label: "Entry Type", value: meta?.entry_type },
                              { label: "Entry", value: meta?.entry },
                              { label: "Breach Type", value: meta?.breach_type },
                              { label: "Event Date", value: meta?.event_date },
                              { label: "Event Type", value: meta?.event_type },
                              { label: "Credentials Count", value: meta?.credentials_count },
                              { label: "Earliest Credential", value: meta?.min_log_date },
                              { label: "Latest Credential", value: meta?.max_log_date },
                              { label: "Status", value: meta?.status },
                              { label: "Jira Ticket", value: meta?.jira_ticket },
                            ]
                      } />
                    </Section>
                  </div>
                )}
              </>
            )}

            {tab === "Contents" && (
              <>
                {subLoadingContents && (
                  <div className="mb-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-neutral-300">Checking your subscription…</div>
                )}
                {subErrorContents && (
                  <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">Subscription check failed: {subErrorContents}</div>
                )}
                {hasContentsAccess === false ? (
                  <LockedOverlay
                    preview={<Section title="Credentials"><CredentialsTable rows={MOCK_CREDS} /></Section>}
                    overlay={<ContentsLockedCallout breachType={meta.breach_type} entryType={meta.entry_type} entry={meta.entry} event_date={meta.event_date} onUpgradeClick={onUpgradeClick} onOpenGlobalSearch={onOpenGlobalSearch} />}
                  />
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm text-neutral-400">
                        {meta?.breach_type === "lookalike"
                          ? "Showing look-alike domains with full evidence package."
                          : "Showing credentials scoped to this event's date window and ceilings."}
                      </p>
                      {meta?.breach_type === "lookalike" ? (
                        <button onClick={() => downloadLookalikeCsv(normalizeLookalikeRecords(contents))}
                          disabled={downloadingCsv || !normalizeLookalikeRecords(contents).length}
                          className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
                          <Download className="h-4 w-4" />{downloadingCsv ? "Preparing…" : "Download CSV"}
                        </button>
                      ) : meta?.breach_type === "git_leak" ? (
                        <button onClick={() => handleDownloadGitLeaksCsv(meta?.id)} disabled={downloadingCsv}
                          className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
                          <Download className="h-4 w-4" />{downloadingCsv ? "Preparing…" : "Download CSV"}
                        </button>
                      ) : (
                        <button onClick={handleDownloadCsv} disabled={downloadingCsv || contents.length === 0}
                          className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-50">
                          <Download className="h-4 w-4" />{downloadingCsv ? "Preparing…" : "Download CSV"}
                        </button>
                      )}
                    </div>
                    {loadingContents ? <Loader />
                      : errorContents ? <div className="p-5 text-red-400 text-sm">Error: {errorContents}</div>
                      : meta?.breach_type === "lookalike" ? (
                        <Section title="Domains"><LookalikeCards rows={normalizeLookalikeRecords(contents)} /></Section>
                      ) : meta?.breach_type === "git_leak" ? (
                        <Section title="Exposed Secrets"><GitLeakTable rows={contents} onExpand={handleExpandDescription} /></Section>
                      ) : (
                        <Section title="Credentials"><CredentialsTable rows={contents} /></Section>
                      )}
                  </>
                )}
              </>
            )}
          </div>
        </aside>, document.body
      )}
    </>
  );
}

/* ================================================================
   UI PRIMITIVES
   ================================================================ */

function SmallSpinBtn({ onClick, loading, icon }) {
  return (
    <button onClick={onClick} disabled={loading} aria-busy={loading}
      className="inline-flex items-center gap-2 rounded-md border border-sky-600/40 bg-sky-500/10 px-2 py-1 text-xs text-sky-200 hover:bg-sky-500/20 disabled:opacity-60 disabled:cursor-not-allowed">
      {loading ? (
        <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      ) : <span className="flex items-center text-sky-300">{icon}</span>}
    </button>
  );
}

function StatusBanner({ status, onDismiss }) {
  if (!status) return null;
  const colors = status.type === "success" ? "border-green-500/30 bg-green-500/10 text-green-200"
    : status.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-200"
    : "border-yellow-500/30 bg-yellow-500/10 text-yellow-200";
  return (
    <div className={`mx-5 mt-3 flex items-center justify-between rounded-md border px-3 py-2 text-sm ${colors}`}>
      <span>
        {status.message}
        {status.issueUrl && (<> — <a href={status.issueUrl} target="_blank" rel="noreferrer" className="underline">Open in Jira</a></>)}
      </span>
      <button onClick={onDismiss} className="ml-3 text-xs text-neutral-400 hover:text-white">Dismiss</button>
    </div>
  );
}

function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button className={`p-2 rounded ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"}`}
      title={title} onClick={onClick} disabled={disabled}>{children}</button>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold tracking-wide text-neutral-400 uppercase">{title}</h3>
      {children}
    </section>
  );
}

function MetaGrid({ items = [] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map(({ label, value }, idx) => (
        <div key={`${label}-${idx}`} className="p-3">
          <dt className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</dt>
          <dd className="mt-1 text-sm text-neutral-100">{value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Loader() {
  return (
    <div className="p-5 space-y-5">
      <div className="h-5 w-36 bg-neutral-800/60 animate-pulse rounded" />
      <div className="space-y-3"><div className="h-4 w-64 bg-neutral-800/60 animate-pulse rounded" /><div className="h-4 w-48 bg-neutral-800/60 animate-pulse rounded" /></div>
      <div className="space-y-2"><div className="h-4 w-full bg-neutral-800/60 animate-pulse rounded" /><div className="h-4 w-5/6 bg-neutral-800/60 animate-pulse rounded" /></div>
    </div>
  );
}

function CopyBtn({ text }) {
  return (
    <button onClick={() => navigator.clipboard.writeText(String(text))} className="p-1 rounded hover:bg-white/5" title="Copy to clipboard">
      <Copy className="h-4 w-4 text-neutral-400 hover:text-white" />
    </button>
  );
}

function PasswordCell({ password }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <span className="text-neutral-100">{visible ? (password || "—") : "••••••••"}</span>
      <button onClick={() => setVisible((v) => !v)} className="p-1 rounded hover:bg-white/5" title={visible ? "Hide" : "Show"}>
        {visible ? <EyeOff className="h-4 w-4 text-neutral-400 hover:text-white" /> : <Eye className="h-4 w-4 text-neutral-400 hover:text-white" />}
      </button>
    </div>
  );
}

function KV({ k, v, isMono = false }) {
  const val = v && String(v).trim().length ? String(v) : "—";
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-neutral-400">{k}</dt>
      <dd className={`mt-0.5 text-neutral-100 break-words ${isMono ? "font-mono text-[12px]" : ""}`}>{val}</dd>
    </div>
  );
}

/* ================================================================
   EVIDENCE SECTION COMPONENT (for collapsible sections inside cards)
   ================================================================ */

function EvidenceSection({ icon, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-neutral-800/60">
      <button onClick={() => setOpen(!open)} type="button"
        className="flex w-full items-center gap-2 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-neutral-400 hover:text-neutral-200 transition-colors">
        {icon}
        <span className="flex-1">{title}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

/* ================================================================
   LOOKALIKE CARDS — Enhanced with full evidence
   ================================================================ */

function LookalikeCards({ rows }) {
  const [expanded, setExpanded] = useState({});

  if (!rows?.length) {
    return <div className="text-neutral-400 text-sm">No domains found for this event.</div>;
  }

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="grid gap-4">
      {rows.map((r, i) => {
        const id = r.domain || String(i);
        const isOpen = !!expanded[id];

        const hasMX = Array.isArray(r.mx_records) && r.mx_records.length > 0;
        const hasHTTP = r.http_status_code !== null && r.http_status_code !== 0 && r.http_status_code !== undefined;
        const isRedirect = r.http_status_code === 301 || r.http_status_code === 302;
        const hasSSL = r.ssl && !r.ssl.parse_error;
        const hasDNS = r.dns && !r.dns._nxdomain && Object.keys(r.dns).some(k => !k.startsWith("_"));
        const hasFavicon = r.favicon_hash && r.favicon_hash !== "n/a";
        const hasRedirectChain = Array.isArray(r.redirect_chain) && r.redirect_chain.length > 0;
        const hasHeaders = r.response_headers && Object.keys(r.response_headers).length > 0;
        const hasScreenshot = r.screenshot_url && r.screenshot_url !== "n/a";

        // Threat signals
        const signals = [];
        if (hasHTTP && (r.http_status_code >= 200 && r.http_status_code < 400)) signals.push("HTTP Live");
        if (hasMX) signals.push("Mail Ready");
        if (hasSSL) signals.push("SSL Active");
        if (hasFavicon) signals.push("Favicon Present");
        if (isRedirect) signals.push("Redirecting");

        // Quick WHOIS facts
        const quick = r.no_whois ? [] : [
          r.registrar ? `Registrar: ${r.registrar}` : null,
          r.created ? `Created: ${r.created}` : null,
          r.expires ? `Expires: ${r.expires}` : null,
        ].filter(Boolean);

        return (
          <article key={`${id}-${i}`} className="rounded-xl border border-neutral-800 bg-neutral-900/30 overflow-hidden">
            {/* Card Header */}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Domain</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-neutral-100 break-all text-[15px]">{r.domain || "—"}</span>
                    {isRedirect && (
                      <span className="inline-flex items-center gap-1 text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3" /><span className="text-[10px] font-mono uppercase">Redirect</span>
                      </span>
                    )}
                    {hasHTTP && r.http_status_code >= 200 && r.http_status_code < 300 && (
                      <span className="inline-flex items-center gap-1 text-red-400 bg-red-500/10 border border-red-500/20 rounded px-1.5 py-0.5">
                        <Globe className="w-3 h-3" /><span className="text-[10px] font-mono uppercase">Live</span>
                      </span>
                    )}
                  </div>
                  {r.title && (
                    <div className="mt-1 text-xs text-neutral-300 italic break-words" title={r.title}>
                      “{r.title}”
                    </div>
                  )}

                  {/* Signal pills */}
                  {signals.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {signals.map((s, si) => (
                        <span key={si} className="text-[10px] px-2 py-0.5 rounded-full border border-neutral-700/60 bg-neutral-800/40 text-neutral-300 font-medium">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* HTTP + availability line */}
                  <div className="mt-2 text-[11px] font-mono text-neutral-500 tracking-wide">
                    {[
                      !r.no_whois ? "WHOIS" : null,
                      hasMX ? "MX" : null,
                      hasHTTP ? `HTTP ${r.http_status_code}` : null,
                      hasSSL ? "SSL" : null,
                      hasDNS ? "DNS" : null,
                      hasFavicon ? "Favicon" : null,
                      hasScreenshot ? "Screenshot" : null,
                    ].filter(Boolean).join(" · ")}
                  </div>

                  {/* WHOIS quick facts */}
                  {quick.length > 0 && (
                    <div className="mt-2 text-xs text-neutral-400">
                      {quick.join(" · ")}
                    </div>
                  )}
                  {r.no_whois && (
                    <div className="mt-2">
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-neutral-700/40 text-neutral-400">No WHOIS available</span>
                    </div>
                  )}
                </div>

                <div className="shrink-0 flex flex-col items-end gap-2">
                  <button onClick={() => navigator.clipboard.writeText(r.domain)}
                    className="px-2 py-1 text-xs rounded border border-white/10 hover:bg-white/5 text-neutral-300 w-full text-center" title="Copy domain">
                    Copy
                  </button>
                  <button onClick={() => toggle(id)}
                    className="px-2 py-1 text-xs rounded border border-white/10 hover:bg-white/5 text-neutral-200 inline-flex items-center justify-center gap-1 w-full"
                    aria-expanded={isOpen}>
                    {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    {isOpen ? "Collapse" : "Evidence"}
                  </button>
                </div>
              </div>
            </div>

            {/* Expandable Evidence */}
            {isOpen && (
              <div className="px-4 pb-4 space-y-0">

                {/* Screenshot */}
                {hasScreenshot && (
                  <EvidenceSection icon={<Camera className="h-3.5 w-3.5" />} title="Screenshot" defaultOpen={true}>
                    <a href={r.screenshot_url} target="_blank" rel="noopener noreferrer" className="block group">
                      <div className="relative rounded-lg overflow-hidden border border-neutral-800 bg-neutral-950">
                        <img src={r.screenshot_url} alt={`Screenshot of ${r.domain}`} loading="lazy"
                          className="w-full max-h-[300px] object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-neutral-950/90 to-transparent p-3">
                          <span className="text-[11px] text-neutral-300 font-mono">Click to view full size</span>
                        </div>
                      </div>
                    </a>
                  </EvidenceSection>
                )}

                {/* SSL Certificate */}
                {hasSSL && (
                  <EvidenceSection icon={<Lock className="h-3.5 w-3.5" />} title="SSL Certificate">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <KV k="Issuer" v={r.ssl.issuer?.organizationName || r.ssl.issuer?.commonName} />
                      <KV k="Subject" v={r.ssl.subject?.commonName} />
                      <KV k="Valid From" v={r.ssl.not_before} />
                      <KV k="Valid Until" v={r.ssl.not_after} />
                      <KV k="Serial" v={r.ssl.serial_number} isMono />
                      <KV k="Fingerprint (SHA256)" v={r.ssl.sha256_fingerprint ? r.ssl.sha256_fingerprint.substring(0, 32) + "…" : "—"} isMono />
                    </div>
                    {r.ssl.san && r.ssl.san.length > 0 && (
                      <div className="mt-2">
                        <dt className="text-[11px] uppercase tracking-wide text-neutral-400 mb-1">Subject Alt Names ({r.ssl.san.length})</dt>
                        <div className="flex flex-wrap gap-1">
                          {r.ssl.san.slice(0, 8).map((s, si) => (
                            <span key={si} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-neutral-800/60 text-neutral-300 border border-neutral-700/40">
                              {s.value}
                            </span>
                          ))}
                          {r.ssl.san.length > 8 && (
                            <span className="text-[11px] px-1.5 py-0.5 text-neutral-500">+{r.ssl.san.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </EvidenceSection>
                )}

                {/* DNS Records */}
                {hasDNS && (
                  <EvidenceSection icon={<Globe className="h-3.5 w-3.5" />} title="DNS Records">
                    <div className="space-y-2">
                      {["A", "AAAA", "NS", "TXT", "CNAME", "SOA"].map(rtype => {
                        const recs = r.dns[rtype];
                        if (!recs || !recs.length) return null;
                        return (
                          <div key={rtype}>
                            <span className="text-[10px] font-mono uppercase text-neutral-500 tracking-wider">{rtype}</span>
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {recs.map((rec, ri) => (
                                <span key={ri} className="text-[12px] font-mono px-2 py-0.5 rounded bg-neutral-800/50 text-neutral-200 border border-neutral-700/40 break-all">
                                  {rec}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </EvidenceSection>
                )}

                {/* Favicon Hash */}
                {hasFavicon && (
                  <EvidenceSection icon={<Hash className="h-3.5 w-3.5" />} title="Favicon Fingerprint">
                    <div className="flex items-center gap-3">
                      <code className="text-sm font-mono px-3 py-1.5 rounded-lg bg-neutral-800/60 text-sky-300 border border-neutral-700/40">
                        {r.favicon_hash}
                      </code>
                      <CopyBtn text={r.favicon_hash} />
                      <span className="text-[11px] text-neutral-500">MMH3 (Shodan-compatible)</span>
                    </div>
                  </EvidenceSection>
                )}

                {/* Redirect Chain */}
                {hasRedirectChain && (
                  <EvidenceSection icon={<ArrowRight className="h-3.5 w-3.5" />} title={`Redirect Chain (${r.redirect_chain.length} hops)`}>
                    <div className="space-y-1">
                      {r.redirect_chain.map((hop, hi) => (
                        <div key={hi} className="flex items-start gap-2 text-[12px] font-mono">
                          <span className={`shrink-0 w-10 text-right font-semibold ${
                            hop.status >= 200 && hop.status < 300 ? "text-green-400"
                            : hop.status >= 300 && hop.status < 400 ? "text-amber-400"
                            : hop.error ? "text-red-400" : "text-neutral-400"
                          }`}>
                            {hop.error ? "ERR" : hop.status}
                          </span>
                          <span className="text-neutral-500">→</span>
                          <span className="text-neutral-200 break-all">{hop.url || "—"}</span>
                          {hop.server && <span className="text-neutral-600 shrink-0">({hop.server})</span>}
                        </div>
                      ))}
                    </div>
                  </EvidenceSection>
                )}

                {/* Response Headers */}
                {hasHeaders && (
                  <EvidenceSection icon={<FileText className="h-3.5 w-3.5" />} title="Response Headers">
                    <dl className="grid grid-cols-1 gap-1.5">
                      {Object.entries(r.response_headers).map(([hk, hv]) => (
                        <div key={hk} className="flex items-baseline gap-2 text-[12px]">
                          <span className="font-mono text-neutral-400 shrink-0">{hk}:</span>
                          <span className="font-mono text-neutral-200 break-all">
                            {Array.isArray(hv) ? hv.join(" | ") : String(hv)}
                          </span>
                        </div>
                      ))}
                    </dl>
                  </EvidenceSection>
                )}

                {/* MX Records */}
                {hasMX && (
                  <EvidenceSection icon={<Server className="h-3.5 w-3.5" />} title="MX Records">
                    <ul className="grid gap-1.5">
                      {r.mx_records.map((mx, idx) => (
                        <li key={idx} className="flex items-center justify-between text-sm text-neutral-300 font-mono bg-neutral-900/50 px-3 py-2 rounded border border-neutral-800/80">
                          <span>{mx.value}</span>
                          <span className="text-neutral-500 text-xs bg-neutral-800 px-1.5 py-0.5 rounded">Priority {mx.priority}</span>
                        </li>
                      ))}
                    </ul>
                  </EvidenceSection>
                )}

                {/* WHOIS Data */}
                {!r.no_whois && (
                  <EvidenceSection icon={<Shield className="h-3.5 w-3.5" />} title="WHOIS Data">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <KV k="Registrar" v={r.registrar} />
                      <KV k="Registrar URL" v={r.registrar_url} isMono />
                      <KV k="WHOIS Server" v={r.whois_server} />
                      <KV k="Created" v={r.created} />
                      <KV k="Updated" v={r.updated} />
                      <KV k="Expires" v={r.expires} />
                      <KV k="Name Servers" v={r.name_servers} isMono />
                      <KV k="Abuse Emails" v={r.abuse_emails} isMono />
                      <KV k="DNSSEC" v={r.dnssec} />
                    </dl>
                  </EvidenceSection>
                )}

                {r.no_whois && !hasMX && !hasSSL && !hasDNS && !hasFavicon && !hasRedirectChain && !hasHeaders && !hasScreenshot && (
                  <div className="py-3 text-sm text-neutral-500 italic">No additional evidence to display.</div>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}


/* ================================================================
   GIT LEAK TABLE (unchanged)
   ================================================================ */

function GitLeakTable({ rows, onExpand }) {
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState({});
  if (!rows?.length) return <div className="text-neutral-400 text-sm">No git leak findings available.</div>;

  const toggle = async (id) => {
    if (expanded[id]?.open) { setExpanded((p) => ({ ...p, [id]: { ...p[id], open: false } })); return; }
    if (expanded[id]?.data) { setExpanded((p) => ({ ...p, [id]: { ...p[id], open: true } })); return; }
    setLoading((p) => ({ ...p, [id]: true }));
    try {
      const data = await onExpand(id);
      setExpanded((p) => ({ ...p, [id]: { open: true, data } }));
    } catch (err) { setExpanded((p) => ({ ...p, [id]: { open: true, data: { error: "Failed to load details." } } })); }
    finally { setLoading((p) => ({ ...p, [id]: false })); }
  };

  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(420px,1fr))]">
      {rows.map((r, i) => {
        const id = r.id || String(i);
        const item = expanded[id]; const isOpen = !!item?.open; const details = item?.data;
        return (
          <article key={`${id}-${i}`} className="rounded-xl border border-neutral-800 bg-neutral-900/30 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-neutral-300">
                  <span className="text-neutral-400">Finding ID:</span> <span className="font-medium text-neutral-100">{r.id}</span>
                  <span className="text-neutral-400"> Rule:</span> <span className="font-medium text-sky-300">{r.rule_id}</span>
                </div>
                <p className="mt-1.5 text-sm text-neutral-300 leading-relaxed">{r.description || "No description available."}</p>
              </div>
              <button onClick={() => toggle(id)} className="px-2 py-1 text-xs rounded border border-white/10 hover:bg-white/5 text-neutral-200 inline-flex items-center gap-1"
                aria-expanded={isOpen}>
                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {loading[id] ? "Loading…" : isOpen ? "Hide" : "Details"}
              </button>
            </div>
            {isOpen && (
              <div className="mt-3 rounded-lg bg-neutral-900/40 p-3 text-sm text-neutral-300 break-words">
                {details?.error ? <span className="text-red-400">{details.error}</span>
                  : details?.finding ? (
                    <div className="space-y-2">
                      {[["Repository", details.finding.repository], ["Branch", details.finding.branch], ["File", details.finding.file],
                        ["Lines", `${details.finding.start_line} – ${details.finding.end_line}`], ["Commit", details.finding.commit],
                        ["Author", details.finding.author], ["Rule", details.finding.rule_id], ["Match", details.finding.match],
                      ].map(([k, v]) => <div key={k}><span className="text-neutral-400">{k}:</span> <span className="text-neutral-100">{v}</span></div>)}
                      <div><span className="text-neutral-400">Secret:</span> <code className="bg-neutral-800/60 px-1 rounded text-red-400">{details.finding.secret}</code></div>
                      <div><span className="text-neutral-400">Detected:</span> <span className="text-neutral-100">{details.finding.detected_at}</span></div>
                    </div>
                  ) : loading[id] ? "Loading…" : "No further information."}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}


/* ================================================================
   CREDENTIALS TABLE (unchanged)
   ================================================================ */

function CredentialsTable({ rows }) {
  const safe = Array.isArray(rows) ? rows : [];
  return (
    <div className="rounded-lg border border-neutral-800">
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-900/60 text-neutral-300 sticky top-0 z-10">
            <tr><Th>Application</Th><Th>Username</Th><Th>Password</Th><Th>Log Date</Th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {safe.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-neutral-400 py-6">No credentials available.</td></tr>
            ) : safe.map((r, i) => (
              <tr key={i} className="hover:bg-white/5">
                <Td className="font-medium text-neutral-100 truncate max-w-[280px]">
                  <div className="flex items-center gap-2"><span className="truncate">{r.url || "—"}</span>{r.url && <CopyBtn text={r.url} />}</div>
                </Td>
                <Td className="text-neutral-100">
                  <div className="flex items-center gap-2"><span className="truncate">{r.username || "—"}</span>{r.username && <CopyBtn text={r.username} />}</div>
                </Td>
                <Td>
                  <div className="flex items-center gap-2"><PasswordCell password={r.password || ""} />{r.password && <CopyBtn text={r.password} />}</div>
                </Td>
                <Td>{fmtISO(r.log_date)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="px-3 py-2 text-left font-semibold">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>; }


/* ================================================================
   LOCKED OVERLAY + CALLOUT (unchanged)
   ================================================================ */

function LockedOverlay({ preview, overlay }) {
  return (
    <div className="relative">
      <div className="pointer-events-none select-none blur-sm">{preview}</div>
      <div className="absolute inset-0 z-10 flex items-center p-4 pointer-events-auto">{overlay}</div>
    </div>
  );
}

function ContentsLockedCallout({ breachType, entryType, entry, event_date, onUpgradeClick, onOpenGlobalSearch }) {
  const goUpgrade = () => { if (typeof onUpgradeClick === "function") return onUpgradeClick(); try { window.location.href = "/billing"; } catch {} };
  const mapBreachTypeToDataset = (t = "") => {
    const key = String(t || "").trim().toLowerCase();
    if (key === "consumer") return "consumerRecords";
    if (key === "corporate") return "corporateRecords";
    if (key === "email") return "emailAddress";
    return "consumerRecords";
  };
  const normalizeQuery = (val = "", dataset = "") => {
    const v = String(val || "").trim(); if (!v) return "";
    if (dataset === "emailAddress") return v;
    try { if (v.includes("@")) return v.split("@")[1].toLowerCase(); const u = new URL(v.includes("://") ? v : `http://${v}`); let h = u.hostname.toLowerCase(); if (h.startsWith("www.")) h = h.slice(4); return h.split(":")[0]; } catch { return v; }
  };
  const goSearch = () => {
    let type = mapBreachTypeToDataset(breachType);
    if (entryType === "email") type = "emailAddress";
    else if (entryType === "ip") type = "ipAddress";
    else if (entryType === "computername") type = "computerName";
    const query = normalizeQuery(entry, type);
    const params = new URLSearchParams({ type, query, event_date });
    if (typeof onOpenGlobalSearch === "function") return onOpenGlobalSearch(`/search?${params.toString()}`, { type, query });
    try { window.location.href = `/search?${params.toString()}`; } catch {}
  };

  return (
    <div className="max-w-lg w-full rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-amber-100 shadow-lg backdrop-blur-md">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 mt-0.5" />
        <div>
          <div className="font-semibold">Event contents are locked</div>
          <div className="text-sm opacity-90 mt-1">
            Your current subscription doesn't include access to credentials through event contents. Please upgrade to Enterprise or Threat Intel, or view related data in Global Search.
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={goUpgrade} className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10">Upgrade plan</button>
            <button onClick={goSearch} className="rounded-md border border-sky-600/40 bg-sky-500/10 px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/20">Open Global Search</button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ================================================================
   NORMALIZE LOOKALIKE — with full evidence extraction
   ================================================================ */

export function normalizeLookalikeRecords(contents) {
  const recsObj =
    contents?.results?.records ||
    contents?.records ||
    (Array.isArray(contents) ? null : contents);

  if (!recsObj || typeof recsObj !== "object") return [];

  return Object.entries(recsObj).map(([domainKey, r]) => {
    const whoisData = r?.whois;
    const mxRecords = r?.mx_records || [];
    const httpCode = r?.http_status_code || 0;

    const pickEvidence = (newVal, legacyVal) => {
      if (newVal !== undefined && newVal !== null) return newVal;
      if (legacyVal === undefined || legacyVal === null) return null;
      return typeof legacyVal === "string" ? safeJsonParse(legacyVal) : legacyVal;
    };

    const sslData = pickEvidence(r?.ssl, r?.ssl_json);
    const dnsData = pickEvidence(r?.dns, r?.dns_json);
    const redirectChain = pickEvidence(r?.redirect_chain, r?.redirect_chain_json);
    const responseHeaders = pickEvidence(r?.response_headers, r?.response_headers_json);
    const faviconHash = r?.favicon_hash || null;
    const screenshotUrl = r?.screenshot_url || null;
    const pageTitle = r?.title || null;

    const noWhois = !whoisData || (Array.isArray(whoisData) && whoisData.length === 0) || Object.keys(whoisData).length === 0;

    const normStr = (x) => (x == null || x === "" ? "—" : String(x));
    const joinArr = (a) => (Array.isArray(a) ? a.filter(Boolean).join(", ") : normStr(a));
    const toISO = (v) => {
      if (Array.isArray(v)) v = v[0];
      if (typeof v === "number") { const ms = v > 2_000_000_000 ? v : v * 1000; const d = new Date(ms); return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10); }
      if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
    };

    const base = {
      domain: String(domainKey || "—"),
      title: pageTitle,
      mx_records: mxRecords,
      http_status_code: httpCode,
      ssl: sslData,
      dns: dnsData,
      favicon_hash: faviconHash,
      redirect_chain: redirectChain,
      response_headers: responseHeaders,
      screenshot_url: screenshotUrl,
    };

    if (noWhois) {
      return { ...base, no_whois: true, registrar: "", registrar_url: "", whois_server: "", created: "", updated: "", expires: "", name_servers: "", abuse_emails: "", dnssec: "" };
    }

    return {
      ...base,
      no_whois: false,
      domain: String(domainKey || whoisData.domain_name || "—"),
      registrar: normStr(whoisData.registrar),
      registrar_url: joinArr(whoisData.registrar_url),
      whois_server: normStr(whoisData.whois_server),
      created: toISO(whoisData.creation_date),
      updated: toISO(whoisData.updated_date),
      expires: toISO(whoisData.expiration_date),
      name_servers: joinArr(whoisData.name_servers),
      abuse_emails: normStr(whoisData.registrar_email || whoisData.emails),
      dnssec: normStr(whoisData.dnssec),
    };
  });
}


/* ================================================================
   CSV DOWNLOAD — with all evidence columns
   ================================================================ */

async function downloadLookalikeCsv(rows, filename = "lookalike_domains.csv") {
  const headers = [
    "Domain", "Registrar", "Registrar URL", "WHOIS Server",
    "Created", "Updated", "Expires", "Name Servers", "Abuse Emails", "DNSSEC",
    "HTTP Status", "MX Records",
    "SSL Issuer", "SSL Expires", "SSL Fingerprint",
    "A Records", "NS Records",
    "Favicon Hash", "Redirect Chain", "Server Header", "Screenshot URL",
  ];
  const esc = (s) => `"${String(s ?? "").replaceAll('"', '""')}"`;

  const body = rows.map((r) => {
    const sslIssuer = r.ssl?.issuer?.organizationName || r.ssl?.issuer?.commonName || "";
    const sslExpires = r.ssl?.not_after || "";
    const sslFp = r.ssl?.sha256_fingerprint || "";
    const aRecs = r.dns?.A ? r.dns.A.join(", ") : "";
    const nsRecs = r.dns?.NS ? r.dns.NS.join(", ") : "";
    const mxStr = Array.isArray(r.mx_records) ? r.mx_records.map(m => `${m.value} (p:${m.priority})`).join(", ") : "";
    const redirStr = Array.isArray(r.redirect_chain) ? r.redirect_chain.map(h => `${h.status || "ERR"} ${h.url || ""}`).join(" -> ") : "";
    const serverHdr = r.response_headers?.server || "";

    return [
      r.domain, r.registrar, r.registrar_url, r.whois_server,
      r.created, r.updated, r.expires, r.name_servers, r.abuse_emails, r.dnssec,
      r.http_status_code || "", mxStr,
      sslIssuer, sslExpires, sslFp,
      aRecs, nsRecs,
      r.favicon_hash || "", redirStr, serverHdr, r.screenshot_url || "",
    ].map(esc).join(",");
  }).join("\n");

  const csv = headers.map(esc).join(",") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}


/* ================================================================
   UTILS
   ================================================================ */

async function handleExpandDescription(gitLeakId) {
  try {
    const jwtToken = localStorage.getItem("token");
    if (!jwtToken) return { error: "Authorization token not found." };
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/watchlist/get_git_leak_contents.php", {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
      body: JSON.stringify({ finding_id: gitLeakId }),
    });
    const data = await res.json();
    if (data.success && data.finding) {
      const f = data.finding;
      return { finding: { repository: f.github_repo_name || "—", branch: f.branch || "—", file: f.file || "—", start_line: f.start_line || "—", end_line: f.end_line || "—", commit: f.commit || "—", author: f.author || "—", rule_id: f.rule_id || "—", match: f.match || "—", secret: f.secret || "—", detected_at: f.detected_at || "—" } };
    }
    return { error: data.error || "No details found." };
  } catch (e) { return { error: "Failed to load details." }; }
}

function fmtISO(d) {
  if (!d) return "—"; const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—"; return dt.toISOString().slice(0, 10);
}

function safeCsv(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function safeJsonParse(str) {
  if (!str) return null;
  try { return JSON.parse(str); } catch { return null; }
}

function extractContentsAccess(json) {
  try {
    if (!json || typeof json !== "object") return false;
    const truthy = (v) => v === 1 || v === "1" || v === true || v === "true";
    const keys = ["watchlist_contents", "contents_access", "credentials_access", "creds_access", "credentials", "darkweb_contents", "leak_contents", "threat_feed", "global_credentials", "global_creds", "contents"];
    for (const k of keys) { if (Object.prototype.hasOwnProperty.call(json, k) && truthy(json[k])) return true; }
    for (const nested of [json.data, json.subscription]) {
      if (nested && typeof nested === "object") { for (const k of keys) { if (Object.prototype.hasOwnProperty.call(nested, k) && truthy(nested[k])) return true; } }
    }
  } catch (_) {}
  return false;
}

const MOCK_CREDS = [
  { url: "login.example.com", username: "j***@example.com", password: "********", log_date: "2024-03-22" },
  { url: "portal.acme.io", username: "a***@acme.io", password: "********", log_date: "2024-03-23" },
  { url: "mail.company.net", username: "u***@company.net", password: "********", log_date: "2024-03-24" },
];